import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import type {
  LinkRecord,
  Tag,
  Collection,
  CollectionFavorite,
  Snippet,
  UserExtended,
  AIContext,
  RecentAnswer,
  EmployeeProfile,
  Workspace,
  DC_COMPONENT_LABELS,
} from "winslow-core";
import type { TrackerData } from "../../../hooks/useTracker";
import { AnyARecord } from "dns";

export const TOOLS = {
  // HOME: "home",
  CHAT: "chat",
  ASSISTANT: "assistant",
  KB: "kb",
  DOCUMENTS: "documents",
  POLICY_STUDIO: "policy_studio",
  NEW: "new",
  CONVERSATIONS: "conversations",
  TICKET: "ticket",
  NOTIFICATIONS: "notifications",
  APP: "app",
  PIN: "pin",
  HRIS: "hris",
  SETTINGS: "settings",
  DESIGN: "design",
  TASKS: "tasks",
};

export const TOOL_COLORS = {
  CHAT: "var(--menu-color-chat)",
  HRIS: "var(--menu-color-hris)",
  TASKS: "var(--menu-color-tasks)",
  KB: "var(--menu-color-kb)",
  DOCUMENTS: "var(--menu-color-documents)",
  // POLICY_STUDIO: "#9CA3AF",
  // NEW: "#EAB308",
  // CONVERSATIONS: "#10B981",
  // TICKET: "#EF4444",
};

export const APP_MENUS = {
  EMPLOYEES: "employees",
  EMPLOYEE_NEW: "employee-new",
  FLATTEN: "flatten",
  DOCUMENTS: "documents",
  RESOURCES: "resources",
  ANSWERS: "answers",
  SETTINGS: "settings",
  CUSTOM_PROPERTIES: "custom-properties",
  VIBES: "vibes",
  WORKFLOWS: "workflows",
  UNSELECTED: "unselected",
  TIME_OFF: "time-off",
  COMPANY_CALENDAR: "company-calendar",
  DIRECTORY: "directory",
  PAY_STUBS: "pay-stubs",
  BENEFITS: "benefits",
  EMPLOYEE_TIME_OFF: "employee-time-off",
  EMPLOYEE_WORKFLOWS: "employee-workflows",
  PAY_RESOURCES: "pay-resources",
  PAY_CONFIG: "pay-config",
  PAY_ACTIVITY: "pay-activity",
  PAY_ENROLL: "pay-enroll",
};

export const KB_MENUS = {
  ACTIVITY: "activity",
  ADD: "add",
  MANAGE: "manage",
  PERSONAS: "personas",
  ANALYTICS: "analytics",
  DOCUMENTS: "documents",
};

export type uiStatesType = {
  searchOpen: boolean;
  folderPickerOpen: boolean;
  snippetSidebarOpen: boolean;
  snippetEdited: boolean;
  confirmDiscardSnippetOpen: boolean;
  confirmDeleteSnippetOpen: boolean;
  answerSidebarOpen: boolean;
  employeeProfileOpen: boolean;
  selectedTool: string;
  appSelectedMenu: string;
  kbSelectedMenu: string;
  resourceSidebarOpen: boolean;
  inChat: boolean;
  hrisLocation: string;
  appPath: any;
};

export type AdminAuthState = {
  isAuthenticated: boolean;
  adminSecret: string;
  verifiedAt: Date | null;
};

export type DCBreadcrumb = {
  objectName: DC_COMPONENT_LABELS;
  label: string;
  data: any;
  display: string;
};

export type AppDataState = {
  user: UserExtended | undefined;
  token: string | undefined;
  trackerData: TrackerData | undefined;
  signUpNow: boolean;
  selectedMenu: string | undefined;
  selectedSubMenu: string | undefined;
  selectedMenuId: string | undefined;
  collections: Collection[] | null;
  collectionFavorites: CollectionFavorite[] | null;
  linkRecords: LinkRecord[] | null;
  tags: Tag[] | null;
  memberAdded: boolean;
  uiStates: uiStatesType;
  aiContext: AIContext;
  isMobile: boolean;
  currentCollectionId: string | null;
  editingCollection: Collection | null;
  snippets: Snippet[] | null;
  sidebarSnippet: Snippet | null;
  sidebarAnswer: RecentAnswer | null;
  onDiscardConfirm: any;
  selectedEmployeeId: string | null;
  selectedPersonaId: string | null;
  lastViewedMessageId: number | null;
  mergeConnected: boolean;
  apiDeckConnected: boolean;
  resourcesSearchText: string;
  employees: EmployeeProfile[] | null;
  employeeProfileCache: Record<string, EmployeeProfile>;
  adminAuth: AdminAuthState;
  dcBreadcrumbs: DCBreadcrumb[] | null;
};

const initialState = {
  user: undefined,
  token: undefined,
  trackerData: undefined,
  signUpNow: false,
  selectedMenu: "unselected",
  selectedSubMenu: "unselected",
  selectedMenuId: undefined,
  collections: null,
  collectionFavorites: null,
  linkRecords: null,
  tags: null,
  memberAdded: false,
  uiStates: {
    searchOpen: false,
    folderPickerOpen: false,
    snippetSidebarOpen: false,
    snippetEdited: false,
    confirmDiscardSnippetOpen: false,
    confirmDeleteSnippetOpen: false,
    answerSidebarOpen: false,
    employeeProfileOpen: false,
    selectedTool: TOOLS.CHAT,
    appSelectedMenu: APP_MENUS.EMPLOYEES,
    kbSelectedMenu: KB_MENUS.ACTIVITY,
    resourceSidebarOpen: false,
    inChat: false,
    hrisLocation: "",
    appPath: {},
  },
  aiContext: {
    linkRecordId: undefined,
    message: "",
    prompts: undefined,
    readyToProcess: false,
  },
  isMobile: false,
  currentCollectionId: null,
  editingCollection: null,
  snippets: null,
  sidebarSnippet: null,
  sidebarAnswer: null,
  onDiscardConfirm: null,
  selectedEmployeeId: null,
  selectedPersonaId: null,
  lastViewedMessageId: null,
  mergeConnected: false,
  apiDeckConnected: false,
  resourcesSearchText: "",
  employees: null,
  employeeProfileCache: {},
  adminAuth: {
    isAuthenticated: false,
    adminSecret: "",
    verifiedAt: null,
  },
  dcBreadcrumbs: null,
} as AppDataState;

export const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      const workspace = action.payload?.workspaces?.find(
        (w: Workspace) => w.id === action.payload?.currentWorkspaceId
      );
      state.mergeConnected = workspace?.mergeAccountToken !== null;
      state.apiDeckConnected = workspace?.apiDeckAccountToken !== null;
    },
    clearUser: (state) => {
      state.user = undefined;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearToken: (state) => {
      state.token = undefined;
    },
    setTrackerData: (state, action) => {
      state.trackerData = action.payload;
    },
    setSignUpNow: (state, action) => {
      state.signUpNow = action.payload;
    },
    setSelectedMenu: (state, action) => {
      state.selectedMenu = action.payload;
      state.uiStates = {
        ...state.uiStates,
        answerSidebarOpen: false,
        snippetSidebarOpen: false,
      };
    },
    setSelectedSubMenu: (state, action) => {
      state.selectedSubMenu = action.payload;
    },
    setSelectedMenuId: (state, action) => {
      state.selectedMenuId = action.payload;
    },
    setCollections: (state, action) => {
      state.collections = action.payload;
    },
    setCollectionFavorites: (state, action) => {
      state.collectionFavorites = action.payload;
    },
    setLinkRecords: (state, action) => {
      state.linkRecords = action.payload;
    },
    setTags: (state, action) => {
      state.tags = action.payload;
    },
    setLinkRecordTitle: (state, action) => {
      if (state.linkRecords) {
        const lrIndex = state.linkRecords.findIndex((linkRecord: any) => {
          return linkRecord.id === action.payload.linkRecordId;
        });

        if (lrIndex !== -1) {
          const linkRecord = state.linkRecords[lrIndex];

          if (linkRecord && linkRecord.links && linkRecord.links.length > 0) {
            linkRecord.links[0].title = action.payload.title;
          }
        }
      }
    },
    setDeleteLinkRecord: (state, action) => {
      if (state.linkRecords) {
        const linkRecordIndex = state.linkRecords.findIndex(
          (linkRecord: any) => {
            return linkRecord.id === action.payload;
          }
        );

        if (linkRecordIndex !== -1) {
          state.linkRecords.splice(linkRecordIndex, 1);
        }
      }
    },
    // not only do we need to chnage the core linkRecord array
    // we need to change all references to that linkRecord in the shares
    // array too to keep them in sync
    updateLinkRecord: (state, action) => {
      if (state.linkRecords) {
        const index = state.linkRecords.findIndex((linkRecord: any) => {
          return linkRecord.id === action.payload.id;
        });

        if (index !== -1) {
          state.linkRecords[index] = { ...action.payload };
        }
      }
    },
    updateTagName: (state, action) => {
      if (state.tags) {
        const { tagId, name } = action.payload;

        const index = state.tags.findIndex((tag: any) => {
          return tag.id === tagId;
        });

        if (index !== -1) {
          state.tags[index].name = name;
        }
      }
    },
    setUIStates: (state, action) => {
      state.uiStates = { ...action.payload };
    },
    setAIContext: (state, action) => {
      state.aiContext = { ...action.payload };
    },
    setMemberAdded: (state, action) => {
      state.memberAdded = action.payload;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setCurrentCollectionId: (state, action) => {
      state.currentCollectionId = action.payload;
    },
    setEditingCollection: (state, action) => {
      state.editingCollection = action.payload;
    },
    setSnippets: (state, action) => {
      state.snippets = action.payload;
    },
    setSidebarSnippet: (state, action) => {
      state.sidebarSnippet = action.payload;
    },
    setOnDiscardConfirm: (state, action) => {
      state.onDiscardConfirm = action.payload;
    },
    setSidebarAnswer: (state, action) => {
      state.sidebarAnswer = action.payload;
    },
    setSelectedEmployeeId: (state, action) => {
      state.selectedEmployeeId = action.payload;
    },
    setSelectedPersonaId: (state, action) => {
      state.selectedPersonaId = action.payload;
    },
    setLastViewedMessageId: (state, action) => {
      state.lastViewedMessageId = action.payload;
    },
    setMergeConnected: (state, action) => {
      state.mergeConnected = action.payload;
    },
    setApiDeckConnected: (state, action) => {
      state.apiDeckConnected = action.payload;
    },
    setResourcesSearchText: (state, action) => {
      state.resourcesSearchText = action.payload;
    },
    setEmployees: (state, action) => {
      state.employees = action.payload;
    },
    setEmployeeProfileCache: (state, action) => {
      const { email, profile } = action.payload;
      state.employeeProfileCache[email] = profile;
    },

    clearEmployeeProfileCache: (state) => {
      state.employeeProfileCache = {};
    },
    setAdminAuth: (state, action) => {
      state.adminAuth = action.payload;
    },
    clearAdminAuth: (state) => {
      state.adminAuth = {
        isAuthenticated: false,
        adminSecret: "",
        verifiedAt: null,
      };
    },
    setDCBreadcrumbs: (state, action) => {
      state.dcBreadcrumbs = action.payload;
    },
    clearWorkspaceData: (state) => {
      // Clear all workspace-scoped data to prevent stale data when switching workspaces
      state.collections = null;
      state.collectionFavorites = null;
      state.linkRecords = null;
      state.tags = null;
      state.snippets = null;
      state.currentCollectionId = null;
      state.editingCollection = null;
      state.sidebarSnippet = null;
      state.sidebarAnswer = null;
      state.employees = null;
      state.selectedEmployeeId = null;
      state.selectedPersonaId = null;
      state.resourcesSearchText = "";
    },
  },
});

export const {
  setUser,
  clearUser,
  setToken,
  clearToken,
  setTrackerData,
  setSignUpNow,
  setSelectedMenu,
  setSelectedSubMenu,
  setSelectedMenuId,
  setCollections,
  setCollectionFavorites,
  setLinkRecords,
  setTags,
  setLinkRecordTitle,
  setDeleteLinkRecord,
  updateLinkRecord,
  updateTagName,
  setUIStates,
  setAIContext,
  setMemberAdded,
  setIsMobile,
  setCurrentCollectionId,
  setEditingCollection,
  setSnippets,
  setSidebarSnippet,
  setSidebarAnswer,
  setOnDiscardConfirm,
  setSelectedEmployeeId,
  setSelectedPersonaId,
  setLastViewedMessageId,
  setMergeConnected,
  setApiDeckConnected,
  setResourcesSearchText,
  setEmployees,
  setEmployeeProfileCache,
  clearEmployeeProfileCache,
  setAdminAuth,
  clearAdminAuth,
  setDCBreadcrumbs,
  clearWorkspaceData,
} = appDataSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectUser = (state: RootState) => state.appData.user;
export const selectCurrentWorkspace = (state: RootState) => {
  const user = state.appData.user;
  if (!user?.currentWorkspaceId) {
    return null;
  }

  return (
    user.workspaces?.find(
      (workspace: Workspace) => workspace.id === user.currentWorkspaceId
    ) || null
  );
};
export const selectHasNativeHRIS = (state: RootState) =>
  selectCurrentWorkspace(state)?.hasNativeHRIS ?? false;
export const selectToken = (state: RootState) => state.appData.token;
export const selectTrackerData = (state: RootState) =>
  state.appData.trackerData;
export const selectSignupNow = (state: RootState) => state.appData.signUpNow;
export const selectSelectedMenu = (state: RootState) =>
  state.appData.selectedMenu;
export const selectSelectedSubMenu = (state: RootState) =>
  state.appData.selectedSubMenu;
export const selectSelectedMenuId = (state: RootState) =>
  state.appData.selectedMenuId;
export const selectCollections = (state: RootState) =>
  state.appData.collections;
export const selectCollectionFavorites = (state: RootState) =>
  state.appData.collectionFavorites;
export const selectLinkRecords = (state: RootState) =>
  state.appData.linkRecords;
export const selectTags = (state: RootState) => state.appData.tags;
export const selectUIStates = (state: RootState) => state.appData.uiStates;
export const selectAIContext = (state: RootState) => state.appData.aiContext;
export const selectMemberAdded = (state: RootState) =>
  state.appData.memberAdded;
export const selectIsMobile = (state: RootState) => state.appData.isMobile;
export const selectCurrentCollectionId = (state: RootState) =>
  state.appData.currentCollectionId;
export const selectEditingCollection = (state: RootState) =>
  state.appData.editingCollection;
export const selectSnippets = (state: RootState) => state.appData.snippets;
export const selectSidebarSnippet = (state: RootState) =>
  state.appData.sidebarSnippet;
export const selectSidebarAnswer = (state: RootState) =>
  state.appData.sidebarAnswer;
export const selectOnDiscardConfirm = (state: RootState) =>
  state.appData.onDiscardConfirm;
export default appDataSlice.reducer;
export const selectSelectedEmployeeId = (state: RootState) =>
  state.appData.selectedEmployeeId;
export const selectSelectedPersonaId = (state: RootState) =>
  state.appData.selectedPersonaId;
export const selectLastViewedMessageId = (state: RootState) =>
  state.appData.lastViewedMessageId;
export const selectMergeConnected = (state: RootState) =>
  state.appData.mergeConnected;
export const selectApiDeckConnected = (state: RootState) =>
  state.appData.apiDeckConnected;
export const selectHrisConnected = (state: RootState) =>
  state.appData.mergeConnected ||
  state.appData.apiDeckConnected ||
  selectHasNativeHRIS(state);
export const selectResourcesSearchText = (state: RootState) =>
  state.appData.resourcesSearchText;
export const selectEmployees = (state: RootState) => state.appData.employees;
export const selectEmployeeProfileCache = (state: RootState) =>
  state.appData.employeeProfileCache;
export const selectEmployeeProfileFromCache =
  (email: string) => (state: RootState) => {
    if (!email || typeof email !== "string") {
      return null;
    }

    const cache = state.appData?.employeeProfileCache;
    if (!cache || typeof cache !== "object") {
      return null;
    }

    return cache[email] || null;
  };

export const selectAdminAuth = (state: RootState) =>
  state.appData.adminAuth || {
    isAuthenticated: false,
    adminSecret: "",
    verifiedAt: null,
  };
export const selectIsAdminAuthenticated = (state: RootState) =>
  state.appData.adminAuth?.isAuthenticated || false;
export const selectAdminSecret = (state: RootState) =>
  state.appData.adminAuth?.adminSecret || "";
export const selectDCBreadcrumbs = (state: RootState) =>
  state.appData.dcBreadcrumbs;
